import React, { useEffect, useMemo, useState } from "react";
import { Line, Doughnut } from "react-chartjs-2";
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
} from "chart.js";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowUpRight,
  Wallet,
  Home,
  Building2,
  AlertCircle,
  Users,
  ClipboardCheck,
  Plus,
  FileText,
  Calendar,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import axiosInstance from "../../services/api/axios";
import { useUser } from "../../context/UserContext";

Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title
);

/* ---------- formatting helpers ---------- */
const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
// Percentages: keep 2 decimals, truncate (never round up) so a value of
// 73.4567 reads as "73.45%", not "73.46%". The trailing zero is preserved
// for visual stability (73.40 → "73.40%").
const fmtPct = (n) => {
  const v = Number(n || 0);
  const truncated = Math.trunc(v * 100) / 100;
  return truncated.toFixed(2);
};
// Signed variant — used by the delta pill so positive values get a leading "+".
const fmtPctSigned = (n) => {
  const v = Number(n || 0);
  if (v === 0) return "0.00";
  const truncated = Math.trunc(v * 100) / 100;
  return `${truncated > 0 ? "+" : ""}${truncated.toFixed(2)}`;
};
const fmtSqft = (n) => {
  const v = Number(n || 0);
  if (v >= 1e5) return `${(v / 1e5).toFixed(2)}L sqft`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K sqft`;
  return `${Math.round(v)} sqft`;
};
const pctDelta = (current, previous) => {
  if (!previous && !current) return 0;
  if (!previous) return 100;
  return ((current - previous) / Math.abs(previous)) * 100;
};
const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};
const monthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-IN", { month: "short" });
};

/* ---------- subcomponents ---------- */
const DeltaPill = ({ value }) => {
  const v = Number(value || 0);
  const positive = v > 0.5;
  const negative = v < -0.5;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const cls = positive
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : negative
    ? "text-rose-700 bg-rose-50 border-rose-200"
    : "text-slate-600 bg-slate-100 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${cls}`}
    >
      <Icon size={12} />
      {`${fmtPctSigned(v)}%`}
    </span>
  );
};

const KpiTile = ({ icon: Icon, label, value, subtitle, delta, accent = "slate" }) => {
  const accents = {
    flamingo: "bg-flamingo-50 text-flamingo-600",
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-600",
    indigo: "bg-indigo-50 text-indigo-600",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
          <Icon size={20} />
        </div>
        {delta !== undefined && <DeltaPill value={delta} />}
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

const SectionCard = ({ title, subtitle, action, children, padded = true }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    {(title || action) && (
      <div className="flex items-start justify-between gap-3 px-5 pt-5">
        <div>
          {title && <h3 className="text-base font-semibold text-slate-900">{title}</h3>}
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    <div className={padded ? "p-5" : ""}>{children}</div>
  </div>
);

// Render helper for the per-project metric cells in the bottom table.
// Inlined as a plain function (not a React component) so it's pure-data
// and doesn't depend on HMR tracking a separate module-scope symbol —
// avoids the "MiniBarCell is not defined" stale-cache crash we saw earlier.
const MINI_BAR_TONES = {
  flamingo: "from-flamingo-400 to-flamingo-600",
  slate: "from-slate-400 to-slate-700",
  indigo: "from-indigo-400 to-indigo-600",
  emerald: "from-emerald-400 to-emerald-600",
};
function renderMiniBarCell(key, value, tone = "flamingo") {
  const v = Number(value || 0);
  return (
    <td key={key} className="px-3 py-3">
      <div className="flex items-center gap-2">
        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${MINI_BAR_TONES[tone] || MINI_BAR_TONES.flamingo}`}
            style={{ width: `${Math.min(100, Math.max(0, v))}%` }}
          />
        </div>
        <span className="text-xs text-slate-600 tabular-nums">{fmtPct(v)}%</span>
      </div>
    </td>
  );
}

const TileSkeleton = () => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 h-32">
    <Skeleton circle height={40} width={40} />
    <Skeleton height={12} width="70%" className="mt-3" />
    <Skeleton height={28} width="50%" className="mt-2" />
  </div>
);

/* ---------- main component ---------- */
const Dashboard = () => {
  const navigate = useNavigate();
  const { userInfo } = useUser();
  const customerCompany =
    userInfo?.loginIdentifierDetails?.customer?.companyName ||
    userInfo?.loginIdentifierDetails?.companyName ||
    "there";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(""); // "" === All projects

  // Picker source — small list, fetched once.
  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get("/dashboard/projects")
      .then((res) => {
        if (!mounted) return;
        setProjectOptions(Array.isArray(res) ? res : res?.items || []);
      })
      .catch(() => {
        if (mounted) setProjectOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Dashboard payload — refetches whenever the project filter changes.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    const params = selectedProjectId ? { projectId: selectedProjectId } : {};
    axiosInstance
      .get("/dashboard/developer-admin", { params })
      .then((res) => {
        if (mounted) setData(res);
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.detail || err.message || "Failed to load dashboard");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedProjectId]);

  /* ---------- derived ---------- */
  const kpis = data?.kpis;
  const portfolio = data?.portfolio;
  const overdue = data?.overdue;
  const approvals = data?.approvals;

  const revenueDelta = useMemo(
    () => pctDelta(kpis?.totalRevenueCurrentMonth, kpis?.totalRevenuePreviousMonth),
    [kpis]
  );
  const unitsDelta = useMemo(
    () => pctDelta(kpis?.unitsSoldCurrentMonth, kpis?.unitsSoldPreviousMonth),
    [kpis]
  );
  const collectionRate = useMemo(() => {
    const proj = kpis?.collectionProjectedCurrentMonth || 0;
    const rec = kpis?.collectionReceivedCurrentMonth || 0;
    return proj > 0 ? (rec / proj) * 100 : 0;
  }, [kpis]);
  const collectionRatePrev = useMemo(() => {
    const proj = kpis?.collectionProjectedPreviousMonth || 0;
    const rec = kpis?.collectionReceivedPreviousMonth || 0;
    return proj > 0 ? (rec / proj) * 100 : 0;
  }, [kpis]);
  const collectionRateDelta = collectionRate - collectionRatePrev;
  // Lifetime cumulative recovered % — matches the "Amount Recovered" tile from
  // the old dashboard (received ÷ projected, across every installment ever).
  const amountRecoveredLifetime = useMemo(() => {
    const proj = kpis?.collectionProjectedLifetime || 0;
    const rec = kpis?.collectionReceivedLifetime || 0;
    return proj > 0 ? (rec / proj) * 100 : 0;
  }, [kpis]);

  const inventoryUsage = useMemo(() => {
    const inv = portfolio?.sqftInventory || 0;
    const sold = portfolio?.sqftSold || 0;
    return inv > 0 ? (sold / inv) * 100 : 0;
  }, [portfolio]);

  const conversionRate = useMemo(() => {
    const leads = portfolio?.leadsCount || 0;
    const conv = portfolio?.conversionCount || 0;
    const total = leads + conv;
    return total > 0 ? (conv / total) * 100 : 0;
  }, [portfolio]);

  /* ---------- chart datasets ---------- */
  const trend = data?.salesTrend || [];
  const trendData = useMemo(
    () => ({
      labels: trend.map((p) => monthLabel(p.month)),
      datasets: [
        {
          label: "Revenue",
          data: trend.map((p) => Number(p.revenue || 0)),
          borderColor: "#f97316", // flamingo-500
          backgroundColor: "rgba(249, 115, 22, 0.12)",
          borderWidth: 2,
          tension: 0.35,
          fill: true,
          yAxisID: "y",
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: "Collected",
          data: trend.map((p) => Number(p.received || 0)),
          borderColor: "#475569", // slate-600
          backgroundColor: "rgba(71, 85, 105, 0.08)",
          borderWidth: 2,
          tension: 0.35,
          fill: false,
          yAxisID: "y",
          borderDash: [4, 4],
          pointRadius: 3,
        },
      ],
    }),
    [trend]
  );
  const trendOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 } },
        },
        tooltip: {
          callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtINR(ctx.parsed.y)}` },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: {
          beginAtZero: true,
          ticks: { callback: (v) => fmtINR(v), font: { size: 11 } },
          grid: { color: "#f1f5f9" },
        },
      },
    }),
    []
  );

  const unitsSold = portfolio?.unitsSold || 0;
  const unitsUnsold = portfolio?.unitsUnsold || 0;
  const unitsTotal = unitsSold + unitsUnsold;
  const soldSharePct = unitsTotal > 0 ? (unitsSold / unitsTotal) * 100 : 0;

  const donutData = useMemo(
    () => ({
      labels: ["Sold", "Available"],
      datasets: [
        {
          data: [unitsSold, unitsUnsold],
          backgroundColor: ["#f97316", "#e2e8f0"],
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    }),
    [unitsSold, unitsUnsold]
  );
  const donutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmtInt(ctx.parsed)} units`,
          },
        },
      },
    }),
    []
  );

  const selectedProjectLabel =
    selectedProjectId === ""
      ? "All projects"
      : projectOptions.find((p) => String(p.projectId) === String(selectedProjectId))?.projectName ||
        "Project";

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* ---- HERO ---- */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300">
              {greeting()}, {customerCompany}
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">
              Here's how your portfolio is doing
            </h1>
            <p className="text-xs text-slate-300 mt-1 flex items-center gap-1.5">
              <Calendar size={12} />
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              <span className="mx-1.5 text-slate-500">•</span>
              {selectedProjectLabel}
            </p>
          </div>

          {/* Project picker — defaults to "All projects". Switching updates every section. */}
          <div className="flex flex-col gap-1.5 min-w-[14rem]">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">
              Filter by project
            </label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
            >
              <option value="" className="text-slate-900">
                All projects
              </option>
              {projectOptions.map((p) => (
                <option key={p.projectId} value={p.projectId} className="text-slate-900">
                  {p.projectName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* ---- HEADLINE KPI TILES ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
          </>
        ) : (
          <>
            <KpiTile
              icon={Wallet}
              label="Revenue this month"
              value={fmtINR(kpis?.totalRevenueCurrentMonth)}
              subtitle={`vs ${fmtINR(kpis?.totalRevenuePreviousMonth)} last month`}
              delta={revenueDelta}
              accent="flamingo"
            />
            <KpiTile
              icon={Home}
              label="Units sold this month"
              value={fmtInt(kpis?.unitsSoldCurrentMonth)}
              subtitle={`vs ${fmtInt(kpis?.unitsSoldPreviousMonth)} last month`}
              delta={unitsDelta}
              accent="emerald"
            />
            <KpiTile
              icon={TrendingUp}
              label="Collection rate"
              value={`${fmtPct(collectionRate)}%`}
              subtitle={`${fmtINR(kpis?.collectionReceivedCurrentMonth)} of ${fmtINR(
                kpis?.collectionProjectedCurrentMonth
              )}`}
              delta={collectionRateDelta}
              accent="indigo"
            />
            <KpiTile
              icon={AlertCircle}
              label="Overdue collections"
              value={fmtINR(overdue?.overdueAmount)}
              subtitle={`${fmtInt(overdue?.overdueCount)} installment${
                overdue?.overdueCount === 1 ? "" : "s"
              } past due`}
              accent="amber"
            />
          </>
        )}
      </div>

      {/* ---- SECONDARY KPI ROW (portfolio + leads + approvals) ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
          </>
        ) : (
          <>
            <KpiTile
              icon={Building2}
              label="Active projects"
              value={fmtInt(portfolio?.totalProjects)}
              subtitle={`${fmtInt(portfolio?.totalPhases)} phases • ${fmtInt(
                portfolio?.totalUnits
              )} units`}
              accent="slate"
            />
            <KpiTile
              icon={Home}
              label="Inventory sold"
              value={`${fmtPct(inventoryUsage)}%`}
              subtitle={`${fmtSqft(portfolio?.sqftSold)} of ${fmtSqft(portfolio?.sqftInventory)}`}
              accent="flamingo"
            />
            <KpiTile
              icon={Users}
              label="Lead conversion"
              value={`${fmtPct(conversionRate)}%`}
              subtitle={`${fmtInt(portfolio?.conversionCount)} of ${fmtInt(
                (portfolio?.leadsCount || 0) + (portfolio?.conversionCount || 0)
              )} leads`}
              accent="indigo"
            />
            <KpiTile
              icon={ClipboardCheck}
              label="Pending approvals"
              value={fmtInt(approvals?.pendingCount)}
              subtitle={
                approvals?.pendingCount > 0 ? "Action needed" : "All caught up"
              }
              accent={approvals?.pendingCount > 0 ? "amber" : "emerald"}
            />
          </>
        )}
      </div>

      {/* ---- TERTIARY KPI ROW — construction + lifetime recovered (parity with old dashboard) ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
          </>
        ) : (
          <>
            <KpiTile
              icon={Building2}
              label="Sqft completed"
              value={fmtSqft(portfolio?.sqftCompleted)}
              subtitle={
                portfolio?.sqftInventory
                  ? `${fmtPct(
                      ((portfolio?.sqftCompleted || 0) / portfolio.sqftInventory) * 100
                    )}% of inventory`
                  : "no inventory"
              }
              accent="slate"
            />
            <KpiTile
              icon={Wallet}
              label="Amount recovered (lifetime)"
              value={`${fmtPct(amountRecoveredLifetime)}%`}
              subtitle={`${fmtINR(kpis?.collectionReceivedLifetime)} of ${fmtINR(
                kpis?.collectionProjectedLifetime
              )}`}
              accent="emerald"
            />
            <KpiTile
              icon={Home}
              label="Units sold (lifetime)"
              value={fmtInt(portfolio?.unitsSold)}
              subtitle={`${fmtInt(portfolio?.unitsUnsold)} units still available`}
              accent="flamingo"
            />
            <KpiTile
              icon={TrendingUp}
              label="Active leads"
              value={fmtInt(portfolio?.leadsCount)}
              subtitle={`${fmtInt(portfolio?.conversionCount)} conversions so far`}
              accent="indigo"
            />
          </>
        )}
      </div>

      {/* ---- CHARTS ROW ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SectionCard
            title="Sales & collections trend"
            subtitle="Revenue booked vs amount collected (last 6 months)"
            action={
              <button
                type="button"
                onClick={() => navigate("/reports/performance")}
                className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700 inline-flex items-center gap-1"
              >
                View report <ArrowUpRight size={12} />
              </button>
            }
          >
            <div className="h-64">
              {loading ? (
                <Skeleton height={250} />
              ) : trend.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  No sales data yet
                </div>
              ) : (
                <Line data={trendData} options={trendOptions} />
              )}
            </div>
          </SectionCard>
        </div>

        <SectionCard
          title="Unit inventory"
          subtitle={`${fmtInt(portfolio?.totalUnits)} total units`}
        >
          <div className="h-64 flex items-center justify-center relative">
            {loading ? (
              <Skeleton circle height={200} width={200} />
            ) : unitsTotal === 0 ? (
              <div className="text-sm text-slate-500">No units yet</div>
            ) : (
              <>
                <Doughnut data={donutData} options={donutOptions} />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none -translate-y-3">
                  <p className="text-2xl font-bold text-slate-900">{fmtPct(soldSharePct)}%</p>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">sold</p>
                </div>
              </>
            )}
          </div>
        </SectionCard>
      </div>

      {/* ---- BOTTOM ROW: recent sales + action centre ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SectionCard
            title="Recent sales"
            subtitle="Latest bookings across your portfolio"
            action={
              <button
                type="button"
                onClick={() => navigate("/projects")}
                className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700 inline-flex items-center gap-1"
              >
                View all <ArrowUpRight size={12} />
              </button>
            }
            padded={false}
          >
            {loading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={48} />
                ))}
              </div>
            ) : (data?.recentSales || []).length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-500">
                No sales recorded yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.recentSales.map((s) => (
                  <li
                    key={s.customerSalesId}
                    className="px-5 py-3 flex items-center justify-between hover:bg-slate-50/60 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {s.customerName}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {s.projectName} · Unit {s.propertyNo}
                      </p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-sm font-semibold text-slate-900 tabular-nums">
                        {fmtINR(s.basePrice)}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {new Date(s.saleDate).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        {/* Action centre */}
        <SectionCard title="Action centre" subtitle="Shortcuts & alerts">
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate("/create-project")}
              className="flex items-center justify-between p-3 rounded-xl bg-flamingo-50 hover:bg-flamingo-100 transition-colors text-left group"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-flamingo-500 text-white flex items-center justify-center">
                  <Plus size={18} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-900">
                    Create project
                  </span>
                  <span className="block text-xs text-slate-600">Start a new development</span>
                </span>
              </span>
              <ChevronRight size={16} className="text-slate-400 group-hover:text-flamingo-600" />
            </button>

            <button
              type="button"
              onClick={() => navigate("/customers")}
              className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-left group"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-slate-700 text-white flex items-center justify-center">
                  <Users size={18} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Add customer</span>
                  <span className="block text-xs text-slate-600">Onboard a buyer</span>
                </span>
              </span>
              <ChevronRight size={16} className="text-slate-400 group-hover:text-slate-700" />
            </button>

            <button
              type="button"
              onClick={() => navigate("/approvals")}
              className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-left group"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center relative">
                  <ClipboardCheck size={18} />
                  {approvals?.pendingCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {approvals.pendingCount > 9 ? "9+" : approvals.pendingCount}
                    </span>
                  )}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Approvals</span>
                  <span className="block text-xs text-slate-600">
                    {loading
                      ? "…"
                      : approvals?.pendingCount
                      ? `${approvals.pendingCount} awaiting review`
                      : "All caught up"}
                  </span>
                </span>
              </span>
              <ChevronRight size={16} className="text-slate-400 group-hover:text-indigo-600" />
            </button>

            <button
              type="button"
              onClick={() => navigate("/reports/performance")}
              className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-left group"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                  <FileText size={18} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Reports</span>
                  <span className="block text-xs text-slate-600">Sales & performance</span>
                </span>
              </span>
              <ChevronRight size={16} className="text-slate-400 group-hover:text-emerald-600" />
            </button>

            {!loading && overdue?.overdueCount > 0 && (
              <div className="mt-1 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900">
                    <p className="font-semibold">
                      {fmtINR(overdue.overdueAmount)} overdue
                    </p>
                    <p className="text-amber-800/80 mt-0.5">
                      Across {overdue.overdueCount} installment
                      {overdue.overdueCount === 1 ? "" : "s"}. Send demand letters to chase.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* ---- TOP PROJECTS ---- */}
      {selectedProjectId === "" && (data?.topProjects || []).length > 0 && (
        <SectionCard
          title="Project performance"
          subtitle="Per-project metrics carried over from the old doughnut view"
          padded={false}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-5 py-3 font-semibold">Project</th>
                  <th className="text-left px-5 py-3 font-semibold">Units sold</th>
                  <th className="text-left px-5 py-3 font-semibold">Revenue</th>
                  <th className="text-left px-3 py-3 font-semibold">Sqft sold</th>
                  <th className="text-left px-3 py-3 font-semibold">Sqft completed</th>
                  <th className="text-left px-3 py-3 font-semibold">Conversion</th>
                  <th className="text-left px-3 py-3 font-semibold">Recovered</th>
                  <th className="text-left px-5 py-3 font-semibold">Construction</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}>
                        <td colSpan={9} className="px-5 py-3">
                          <Skeleton height={24} />
                        </td>
                      </tr>
                    ))
                  : data.topProjects.map((p) => {
                      const sellThrough =
                        p.unitsTotal > 0 ? (p.unitsSold / p.unitsTotal) * 100 : 0;
                      return (
                        <tr
                          key={p.projectId}
                          onClick={() => navigate(`/project-details/${p.projectId}`)}
                          className="cursor-pointer hover:bg-slate-50/60 transition-colors"
                        >
                          <td className="px-5 py-3">
                            <p className="text-sm font-medium text-slate-900">{p.projectName}</p>
                            <p className="text-[11px] text-slate-500">
                              {p.unitsTotal} total units
                            </p>
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-sm font-semibold text-slate-900 tabular-nums">
                              {fmtInt(p.unitsSold)}
                            </p>
                            <div className="w-24 h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-flamingo-400 to-flamingo-600"
                                style={{ width: `${Math.min(100, sellThrough)}%` }}
                              />
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sm text-slate-700 tabular-nums whitespace-nowrap">
                            {fmtINR(p.revenue)}
                          </td>
                          {renderMiniBarCell(`${p.projectId}-sqftsold`, p.sqftSoldPercent, "flamingo")}
                          {renderMiniBarCell(`${p.projectId}-sqftdone`, p.sqftCompletedPercent, "slate")}
                          {renderMiniBarCell(`${p.projectId}-conv`, p.conversionPercent, "indigo")}
                          {renderMiniBarCell(`${p.projectId}-rec`, p.amountRecoveredPercent, "emerald")}
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-slate-400 to-slate-700"
                                  style={{ width: `${Math.min(100, p.completionPercent)}%` }}
                                />
                              </div>
                              <span className="text-xs text-slate-600 tabular-nums">
                                {fmtPct(p.completionPercent)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-slate-400">
                            <ChevronRight size={16} />
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default Dashboard;
